// Rule-based citation formatting. MVP: users should review and edit before publishing.
// Not guaranteed to be academically perfect.
//
// formatReference() returns { parts, plain, link, linkLabel }
//   parts: array of { t: string, i?: boolean }  (i = italic) for rich rendering
//   plain: plain-text form (for copy / print / verification)
// orderReferences() returns a DISPLAY-ordered copy (never mutates stored data):
//   - APA / MLA / Chicago  -> alphabetical by first author surname, then title
//   - Vancouver / IEEE     -> numbered / citation (insertion) order

import { normalizeAuthors, looksLikeOrg } from "@/lib/authors";

export const CITATION_STYLES = ["APA", "MLA", "Chicago", "Vancouver", "IEEE"];
const NUMBERED = new Set(["Vancouver", "IEEE"]);

export function isNumbered(style) {
  return NUMBERED.has(style);
}

function clean(s) {
  return (s || "").trim();
}
function stripDot(s) {
  return clean(s).replace(/\.\s*$/, "");
}
function endPeriod(s) {
  s = clean(s);
  if (!s) return "";
  return /[.!?]$/.test(s) ? s : s + ".";
}

function buildLink(doi, url) {
  const d = clean(doi);
  const u = clean(url);
  if (d) {
    const bare = d.replace(/^doi:\s*/i, "").replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
    return { link: d.startsWith("http") ? d : `https://doi.org/${bare}`, linkLabel: `https://doi.org/${bare}` };
  }
  return { link: u, linkLabel: u };
}

// Source types the formatter understands (display-only; entered metadata is preserved).
export const SOURCE_TYPES = [
  { value: "journal", label: "Journal Article" },
  { value: "book", label: "Book" },
  { value: "chapter", label: "Book Chapter" },
  { value: "website", label: "Website" },
  { value: "report", label: "Report" },
  { value: "dissertation", label: "Dissertation / Thesis" },
  { value: "conference", label: "Conference Paper" },
  { value: "other", label: "Other" },
];
const BOOK_LIKE = new Set(["book", "chapter", "website", "report", "dissertation", "other"]);

export function resolveSourceType(ref) {
  const t = clean(ref.source_type);
  if (t) return t;
  return clean(ref.source) ? "journal" : "book"; // legacy inference
}

function isBookLike(type) {
  return BOOK_LIKE.has(type);
}

// APA-style sentence case. If the title was entered in Title Case, lowercase everything except the
// first word, the first word after a colon/sentence break, and preserved tokens. If the title is
// ALREADY in sentence case (most words lowercase), preserve its capitalization (proper nouns like
// "Patient Health Questionnaire" stay capitalized) and only fix the first word / after-break caps.
// Always preserves internally-capitalized brand words (iPhone, McGraw-Hill) and ALL-CAPS/dotted
// acronyms (COVID-19, U.S., JAMA, WHO, PHQ-9).
export function sentenceCase(s) {
  const str = clean(s);
  if (!str) return "";
  const words = str.split(/\s+/);
  const caps = words.filter((w) => /^[A-Z]/.test(w)).length;
  const titleCaseInput = words.length > 0 && caps / words.length >= 0.6;
  const lowerWord = (w) => {
    if (!w) return w;
    if (/[a-z]/.test(w) && /[A-Za-z][A-Z]/.test(w)) return w;           // iPhone, McGraw-Hill
    if (/^[A-Z0-9][A-Z0-9.&/-]*$/.test(w) && /[A-Z]/.test(w)) return w; // COVID-19, U.S., JAMA, WHO
    return titleCaseInput ? w.toLowerCase() : w;                        // preserve proper nouns in sentence-case input
  };
  return str
    .split(/((?<=[a-z])[.!?]\s+|:\s+)/)
    .map((seg) => {
      if (/^[.:!?]\s+$/.test(seg)) return seg;
      const lowered = seg.split(/(\s+)/).map(lowerWord).join("");
      return lowered.replace(/[A-Za-z]/, (c) => c.toUpperCase());
    })
    .join("");
}

function norm(s) {
  return clean(s).replace(/\.+$/, "").replace(/\s+/g, " ").toLowerCase();
}
// APA: omit publisher when it is the same as the author.
function samePublisherAsAuthor(publisher, authors) {
  const p = norm(publisher);
  return p !== "" && p === norm(authors);
}

function splitPeople(str) {
  return (str || "").split(/\s*;\s*|\s+and\s+|\s*,?\s*&\s*/i).map((s) => s.trim()).filter(Boolean);
}
function personInitialsFirst(raw) {
  const name = (raw || "").trim().replace(/\.$/, "");
  if (!name) return "";
  let surname = "", given = [];
  if (name.includes(",")) {
    const [s, g] = name.split(",");
    surname = s.trim();
    given = (g || "").trim().split(/[\s.]+/).filter(Boolean);
  } else {
    const t = name.split(/\s+/);
    const last = t[t.length - 1];
    if (t.length > 1 && /^[A-Z]{1,4}$/.test(last)) { surname = t.slice(0, -1).join(" "); given = last.split(""); }
    else { surname = last; given = t.slice(0, -1); }
  }
  const inits = given.map((x) => (x.match(/[A-Za-z]/) ? x[0].toUpperCase() + "." : "")).filter(Boolean).join(" ");
  return inits ? `${inits} ${surname}` : surname;
}
// Editors formatted initials-first (for "In ... (Eds.), ").
function formatEditors(str) {
  const names = splitPeople(str).map(personInitialsFirst).filter(Boolean);
  if (!names.length) return null;
  const text = names.length === 1 ? names[0] : names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
  return { text, label: names.length > 1 ? "Eds." : "Ed." };
}
function editionLabel(ed) {
  const e = clean(ed);
  if (!e) return "";
  return /\bed\.?$/i.test(e) ? e.replace(/\bed$/i, "ed.") : `${e} ed.`;
}
// APA parenthetical date built from normalized fields: (2025), (2025, August), (2025, August 29), (n.d.).
// `yr` lets callers pass a suffix-disambiguated year (e.g., "2025a").
function apaDatePar(ref, yr) {
  const y = yr != null ? clean(yr) : clean(ref.year);
  if (!y) return "";
  const m = clean(ref.month);
  const d = clean(ref.day);
  if (!m) return `(${y})`;
  return d ? `(${y}, ${m} ${d})` : `(${y}, ${m})`;
}

// --- APA 7 same-author/same-year disambiguation (2025a, 2025b, …) ---------------------------
// The letter is a property of the WHOLE list (which entries share author + year), so it is
// computed once over the full list and looked up per reference.
function suffixLetters(n) {
  // 0->a, 25->z, 26->aa, 27->ab, ...
  let s = "";
  n += 1;
  while (n > 0) { n -= 1; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s;
}
function groupAuthorKey(ref) {
  const raw = clean(ref.authors_raw || ref.authors);
  if (!raw) return "";
  let canon = raw;
  try { canon = normalizeAuthors(raw, "APA") || raw; } catch { canon = raw; }
  return canon.toLowerCase().replace(/[.,;&]+/g, " ").replace(/\s+/g, " ").trim();
}
function baseYear(ref) {
  const y = clean(ref.year).toLowerCase();
  if (!y) return "";
  if (/^n\.?\s*d\.?$/.test(y)) return "n.d.";
  const m = y.match(/\d{3,4}/);
  return m ? m[0] : "";
}
function titleSortKey(ref) {
  return clean(ref.title).toLowerCase().replace(/^(a|an|the)\s+/, "");
}
// Append the APA year-suffix letter: numeric years -> "2025a"; no-date -> "n.d.-a".
export function applyYearSuffix(year, suffix) {
  const y = clean(year);
  if (!suffix || !y) return y;
  return /^n\.?\s*d\.?/i.test(y) ? `${y}-${suffix}` : `${y}${suffix}`;
}
// Map<referenceObject, "a"|"b"|…> for every reference that shares author + year with another.
export function yearSuffixMap(refs) {
  const list = Array.isArray(refs) ? refs : [];
  const groups = new Map();
  list.forEach((r, i) => {
    const ak = groupAuthorKey(r);
    const yb = baseYear(r);
    if (!ak || !yb) return; // APA suffixes require a known author AND year
    const key = `${ak}|${yb}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ r, i });
  });
  const map = new Map();
  for (const arr of groups.values()) {
    if (arr.length < 2) continue;
    arr
      .sort((a, b) => titleSortKey(a.r).localeCompare(titleSortKey(b.r)) || a.i - b.i)
      .forEach((o, idx) => map.set(o.r, suffixLetters(idx)));
  }
  return map;
}
// APA 7 dissertation/thesis descriptor.
function workTypeLabel(wt) {
  const w = clean(wt).toLowerCase();
  if (["masters", "master", "master's", "masters thesis", "master's thesis"].includes(w)) return "Master's thesis";
  return "Doctoral dissertation";
}

const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_MLA = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];

function parseDateParts(raw) {
  const s = clean(raw);
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return { y: +iso[1], m: +iso[2], d: +iso[3] };
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
  return null;
}
// Format an accessed/retrieved date per style. Falls back to the raw text if unparseable.
function accessDateText(raw, style) {
  const p = parseDateParts(raw);
  if (!p) return clean(raw);
  if (style === "MLA") return `${p.d} ${MONTHS_MLA[p.m - 1]} ${p.y}`;
  return `${MONTHS_FULL[p.m - 1]} ${p.d}, ${p.y}`; // APA / Chicago
}

export function formatReference(ref, style, yearSuffix) {
  // Display-only author normalization for initials-based styles (APA/Vancouver/IEEE):
  // "Grohol, John M." -> "Grohol, J. M.". Derived from the raw name; stored data is untouched.
  // MLA/Chicago spell out given names, so they keep the entered form.
  const INITIAL_STYLES = new Set(["APA", "Vancouver", "IEEE"]);
  const rawAuthors = clean(ref.authors_raw || ref.authors);
  const authors = INITIAL_STYLES.has(style) ? normalizeAuthors(rawAuthors, style) : rawAuthors;
  // APA 7 year-suffix disambiguation applies to author-date styles; numbered styles (Vancouver/IEEE) keep the plain year.
  const year = NUMBERED.has(style) ? clean(ref.year) : applyYearSuffix(ref.year, yearSuffix);
  const title = clean(ref.title);
  let source = clean(ref.source);
  const volume = clean(ref.volume);
  const issue = clean(ref.issue);
  const pages = clean(ref.pages);
  const publisher = clean(ref.publisher);
  const editors = clean(ref.editors);
  const edition = clean(ref.edition);
  const container = clean(ref.container);
  const institution = clean(ref.institution);
  const conference = clean(ref.conference);
  const location = clean(ref.location);
  const eventDate = clean(ref.event_date);
  const { link, linkLabel } = buildLink(ref.doi, ref.url);
  const type = resolveSourceType(ref);
  const confType = (clean(ref.conf_type).toLowerCase()) || "paper";
  if (type === "conference") source = source || conference; // don't lose the conference name in non-APA styles
  const book = isBookLike(type);
  const edLabel = editionLabel(edition);

  const parts = [];
  const p = (t, i) => { if (t) parts.push(i ? { t, i: true } : { t }); };
  const pubUnlessAuthor = () => {
    if (publisher && !samePublisherAsAuthor(publisher, authors)) p(endPeriod(publisher) + " ");
  };

  if (style === "APA") {
    if (type === "journal" && source) {
      let titleLed = false;
      if (authors) p(endPeriod(authors) + " ");
      else if (title) { p(endPeriod(sentenceCase(title)) + " "); titleLed = true; } // no author: title leads (APA 7)
      const dp = apaDatePar(ref, year);
      if (dp) p(`${dp}. `);
      if (title && !titleLed) p(endPeriod(sentenceCase(title)) + " "); // APA: article title in sentence case
      p(source, true); // journal italic
      if (volume) { p(", "); p(volume, true); } // volume italic
      if (issue) p(`(${issue})`); // issue NOT italic
      if (pages) p(`, ${pages}`);
      p(". ");
    } else {
      // Book / chapter / report / dissertation / conference / website / etc.
      let titleLed = false;
      if (authors) p(endPeriod(authors) + " ");
      else if (title && (type === "book" || type === "website" || type === "other")) {
        // APA 7: a work with no author moves its title into the author position (before the date).
        p(sentenceCase(title), true); p(". "); titleLed = true;
      }
      if (type === "conference" && confType !== "proceedings") {
        // Presentations use "(Year, Month Day–Day)".
        const inner = [year, eventDate].filter(Boolean).join(", ");
        if (inner) p(`(${inner}). `);
      } else {
        const dp = apaDatePar(ref, year);
        if (dp) p(`${dp}. `);
      }
      if (type === "chapter") {
        if (title) p(endPeriod(sentenceCase(title)) + " "); // chapter title: not italic, sentence case
        const eds = formatEditors(editors);
        p("In ");
        if (eds) p(`${eds.text} (${eds.label}), `);
        if (container) p(sentenceCase(container), true); // parent book italic
        const paren = [];
        if (edLabel) paren.push(edLabel);
        if (pages) paren.push(`pp. ${pages}`);
        if (paren.length) p(` (${paren.join(", ")})`);
        p(". ");
        pubUnlessAuthor();
      } else if (type === "dissertation") {
        // APA 7: *Title* (Publication No. XXX) [Doctoral dissertation, Institution]. Repository.  (+ DOI/URL link)
        if (title) p(sentenceCase(title), true); // italic title, sentence case
        const pubNo = clean(ref.report_number);
        if (pubNo) p(` (Publication No. ${pubNo})`);
        const desc = [workTypeLabel(ref.work_type), institution].filter(Boolean).join(", ");
        if (desc) p(` [${desc}]`);
        p(". ");
        if (source) p(endPeriod(source) + " "); // repository / database name — NOT treated as a book publisher
      } else if (type === "report") {
        // APA 7: *Title of report* (Report No. XXX). Publisher.  — report number stays out of the publisher.
        if (title) {
          p(sentenceCase(title), true);
          const rn = clean(ref.report_number);
          if (rn) p(` (Report No. ${rn})`);
          if (edLabel) p(` (${edLabel})`);
          p(". ");
        }
        if (source) p(endPeriod(source) + " ");
        pubUnlessAuthor();
      } else if (type === "conference") {
        if (confType === "proceedings") {
          // APA 7 published proceedings: journal-like (proceedings name italic).
          if (title) p(endPeriod(sentenceCase(title)) + " ");
          const procName = conference || source;
          if (procName) p(procName, true);
          if (volume) { p(", "); p(volume, true); }
          if (issue) p(`(${issue})`);
          if (pages) p(`, ${pages}`);
          p(". ");
          pubUnlessAuthor();
        } else {
          // APA 7 paper/poster presentation: *Title* [Paper presentation]. Conference Name, City, State/Country.
          if (title) p(sentenceCase(title), true); // italic title
          const label = confType === "poster" ? "Poster presentation" : "Paper presentation";
          p(` [${label}]. `);
          const nameLoc = [conference || source, location].filter(Boolean).join(", ");
          if (nameLoc) p(endPeriod(nameLoc) + " "); // conference name/location — NOT a journal/book field
        }
      } else {
        if (title && !titleLed) { p(sentenceCase(title), true); if (edLabel) p(` (${edLabel})`); p(". "); }
        if (source) p(endPeriod(source) + " "); // site name / institution / container
        pubUnlessAuthor();
      }
    }
  } else if (style === "MLA") {
    if (authors) p(endPeriod(authors) + " ");
    if (book) {
      if (title) { p(stripDot(title), true); p(". "); } // book title italic
      const seg = [];
      if (source) seg.push(source);
      const eds = formatEditors(editors);
      if (eds) seg.push(`Edited by ${eds.text}`);
      if (edLabel) seg.push(edLabel);
      if (institution) seg.push(institution);
      if (publisher && !samePublisherAsAuthor(publisher, authors)) seg.push(publisher);
      if (year) seg.push(year);
      if (seg.length) p(seg.join(", ") + ". ");
    } else {
      if (title) p(`"${stripDot(title)}." `);
      p(source, true);
      const seg = [];
      if (volume) seg.push(`vol. ${volume}`);
      if (issue) seg.push(`no. ${issue}`);
      if (year) seg.push(year);
      if (pages) seg.push(`pp. ${pages}`);
      if (seg.length) p(", " + seg.join(", "));
      if (source) p(". ");
    }
  } else if (style === "Chicago") {
    if (authors) p(endPeriod(authors) + " ");
    if (book) {
      if (title) { p(stripDot(title), true); p(". "); }
      const tail = [];
      const eds = formatEditors(editors);
      if (eds) tail.push(`Edited by ${eds.text}`);
      if (edLabel) tail.push(edLabel);
      const pubyear = [];
      if (source) pubyear.push(source);
      if (institution) pubyear.push(institution);
      if (publisher && !samePublisherAsAuthor(publisher, authors)) pubyear.push(publisher);
      if (year) pubyear.push(year);
      if (pubyear.length) tail.push(pubyear.join(", "));
      if (tail.length) p(tail.map((s) => s.replace(/\.$/, "")).join(". ") + ".");
    } else {
      if (title) p(`"${stripDot(title)}." `);
      p(source, true);
      if (volume) p(` ${volume}`);
      if (issue) p(`, no. ${issue}`);
      if (year) p(` (${year})`);
      if (pages) p(`: ${pages}`);
      if (source) p(". ");
    }
  } else if (style === "Vancouver") {
    if (authors) p(endPeriod(authors) + " ");
    if (title) p(endPeriod(title) + " "); // Vancouver: no italics
    if (book) {
      const seg = [];
      if (source) seg.push(source);
      if (edLabel) seg.push(edLabel);
      if (institution) seg.push(institution);
      if (publisher && !samePublisherAsAuthor(publisher, authors)) seg.push(publisher);
      if (year) seg.push(year);
      if (seg.length) p(endPeriod(seg.join("; ")));
    } else {
      let s = "";
      if (source) s += stripDot(source) + ". ";
      if (year) s += year;
      if (volume) s += `;${volume}`;
      if (issue) s += `(${issue})`;
      if (pages) s += `:${pages}`;
      s = s.trim();
      if (s) p(s.endsWith(".") ? s : s + ".");
    }
  } else if (style === "IEEE") {
    if (authors) { const a = clean(authors); p(a.endsWith(".") ? a + " " : a + ", "); }
    if (book) {
      if (title) { p(stripDot(title), true); p(". "); } // book title italic
      const seg = [];
      if (source) seg.push(source);
      if (edLabel) seg.push(edLabel);
      if (institution) seg.push(institution);
      if (publisher && !samePublisherAsAuthor(publisher, authors)) seg.push(publisher);
      if (year) seg.push(year);
      if (seg.length) p(seg.join(", ") + ". ");
    } else {
      if (title) p(`"${stripDot(title)}," `);
      p(source, true);
      const seg = [];
      if (volume) seg.push(`vol. ${volume}`);
      if (issue) seg.push(`no. ${issue}`);
      if (pages) seg.push(`pp. ${pages}`);
      if (year) seg.push(year);
      if (seg.length) p(", " + seg.join(", "));
      if (source) p(". ");
    }
  }

  // Non-APA: keep the conference location/date from being dropped in journal-like rendering.
  if (style !== "APA" && type === "conference") {
    const extra = [eventDate, location].filter(Boolean).join(", ");
    if (extra) p(endPeriod(extra) + " ");
  }

  const plainCore = parts.map((x) => x.t).join("").replace(/\s+/g, " ").trim();
  // Access/retrieved date (APA "Retrieved …, from"; MLA "Accessed …") — only meaningful with a link.
  let linkPrefix = "";
  let linkSuffix = "";
  const accessed = clean(ref.accessed);
  if (accessed && link) {
    if (style === "APA") linkPrefix = `Retrieved ${accessDateText(accessed, "APA")}, from `;
    else if (style === "MLA") linkSuffix = `. Accessed ${accessDateText(accessed, "MLA")}.`;
    else if (style === "Chicago") linkSuffix = `. Accessed ${accessDateText(accessed, "Chicago")}.`;
  }
  const plain = link
    ? `${plainCore} ${linkPrefix}${linkLabel}${linkSuffix}`.replace(/\s+/g, " ").trim()
    : plainCore;
  return { parts, plain, link, linkLabel, linkPrefix, linkSuffix };
}

export function formatReferencePlain(ref, style, yearSuffix) {
  return formatReference(ref, style, yearSuffix).plain;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s) {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

// Structured runs for document export: [{ text, italic, link? }] in reading order.
// Reuses the same formatter, so citation style, italics, hyperlink & order match the page.
export function formatReferenceRuns(ref, style, yearSuffix) {
  const { parts, link, linkLabel, linkPrefix, linkSuffix } = formatReference(ref, style, yearSuffix);
  const runs = parts.map((x) => ({ text: x.t, italic: !!x.i }));
  if (link) {
    const href = /^https?:\/\//i.test(link) ? link : `https://${link}`;
    runs.push({ text: " ", italic: false });
    if (linkPrefix) runs.push({ text: linkPrefix, italic: false });
    runs.push({ text: linkLabel, italic: false, link: href });
    if (linkSuffix) runs.push({ text: linkSuffix, italic: false });
  }
  return runs;
}

// Inline HTML for one reference: preserves italics (<i>) and a real hyperlink (<a>).
// Does NOT include the paragraph wrapper — the caller decides paragraph/indent/spacing.
export function formatReferenceHtml(ref, style, yearSuffix) {
  const { parts, link, linkLabel, linkPrefix, linkSuffix } = formatReference(ref, style, yearSuffix);
  let html = parts
    .map((x) => (x.i ? `<i>${escapeHtml(x.t)}</i>` : escapeHtml(x.t)))
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  if (link) {
    const prefix = linkPrefix ? escapeHtml(linkPrefix) : "";
    const suffix = linkSuffix ? escapeHtml(linkSuffix) : "";
    const href = /^https?:\/\//i.test(link) ? link : `https://${link}`;
    html = `${html} ${prefix}<a href="${escapeAttr(href)}">${escapeHtml(linkLabel)}</a>${suffix}`.replace(/\s+/g, " ").trim();
  }
  return html;
}

// --- APA 7 in-text citations -----------------------------------------------------------------
function surnameOf(person) {
  const name = clean(person).replace(/\.$/, "");
  if (!name) return "";
  if (name.includes(",")) return name.split(",")[0].trim();
  const toks = name.split(/\s+/);
  const last = toks[toks.length - 1];
  if (toks.length > 1 && /^[A-Z]{1,4}$/.test(last)) return toks.slice(0, -1).join(" "); // "Grohol J M"
  return last;
}
function shortTitleWords(ref) {
  const t = clean(ref.title).replace(/\s+/g, " ").replace(/[.:;,]+$/, "");
  return t ? t.split(" ").slice(0, 4).join(" ") : "";
}
// Split an author string into individual people, robust to both the app's canonical ";"-separated
// form AND APA comma style ("Smith, J., Jones, A., & Lee, B."). Mirrors docxImport.fixAuthors.
function splitAuthorsForCount(raw) {
  let s = clean(raw).replace(/,?\s*&\s*/g, "; ").replace(/\s+and\s+/gi, "; ");
  s = s.replace(/([A-Z]\.)\s*,\s*(?=[A-Z][A-Za-z'’.-]+,\s*[A-Z])/g, "$1; ");
  return s.split(/\s*;\s*/).map((x) => x.trim()).filter(Boolean);
}
// Numbered in-text citation for Vancouver/IEEE: IEEE -> "[1]", Vancouver -> "(1)". `n` is 1-based.
export function numberedInText(n, style) {
  return style === "IEEE" ? `[${n}]` : `(${n})`;
}

// Returns { parenthetical, narrative } per APA 7. Reuses author + year-suffix logic; display-only.
export function inTextCitation(ref, opts = {}) {
  const type = resolveSourceType(ref);
  const y = applyYearSuffix(clean(ref.year) || "n.d.", opts.suffix);

  const raw = clean(ref.authors_raw || ref.authors);
  if (!raw) {
    const st = shortTitleWords(ref);
    if (!st) return { parenthetical: `(${y})`, narrative: `(${y})` };
    // APA 7: quote the title in-text only when it is NOT italicized in the reference
    // (journal articles & book chapters). Books/reports/webpages/etc. are italic -> no quotes.
    const quoted = type === "journal" || type === "chapter";
    const lead = quoted ? `"${st}"` : st;
    const parenLead = quoted ? `"${st},"` : `${st},`; // APA: comma inside the closing quote
    return { parenthetical: `(${parenLead} ${y})`, narrative: `${lead} (${y})` };
  }

  let names = looksLikeOrg(raw)
    ? [raw.replace(/\.$/, "")]
    : splitAuthorsForCount(raw).map(surnameOf).filter(Boolean);
  if (!names.length) names = [raw.replace(/\.$/, "")];

  const paren = names.length === 1 ? names[0]
    : names.length === 2 ? `${names[0]} & ${names[1]}`
    : `${names[0]} et al.`;
  const narr = names.length === 1 ? names[0]
    : names.length === 2 ? `${names[0]} and ${names[1]}`
    : `${names[0]} et al.`;
  return { parenthetical: `(${paren}, ${y})`, narrative: `${narr} (${y})` };
}

function firstSurname(authors) {
  const first = (authors || "").split(/;|\s+&\s+|,\s*&\s*/)[0] || "";
  const a = first.trim();
  if (!a) return ""; // caller falls back to title
  // Group/organization authors sort by the FULL name (first word), e.g. "World Health
  // Organization" -> under W, not by the last token "Organization".
  if (looksLikeOrg(a)) return a.toLowerCase().replace(/^the\s+/, "");
  if (a.includes(",")) return a.split(",")[0].trim().toLowerCase();
  const toks = a.split(/\s+/);
  return (toks[toks.length - 1] || "").toLowerCase();
}

// Alphabetization key: author surname, or the title (minus a leading article) when there is no author.
function sortKey(ref) {
  const key = firstSurname(ref.authors_raw || ref.authors);
  if (key) return key;
  const t = (ref.title || "").trim().toLowerCase().replace(/^(a|an|the)\s+/, "");
  return t || "\uffff"; // truly empty entries sink to the end
}

// Returns a display-ordered COPY. Does not mutate the input / stored data.
export function orderReferences(refs, style) {
  const list = Array.isArray(refs) ? refs.slice() : [];
  if (isNumbered(style)) return list; // numbered styles keep stored/citation order
  return list
    .map((r, idx) => ({ r, idx }))
    .sort((x, y) => {
      const s = sortKey(x.r).localeCompare(sortKey(y.r));
      if (s !== 0) return s;
      const t = (x.r.title || "").toLowerCase().localeCompare((y.r.title || "").toLowerCase());
      if (t !== 0) return t;
      return x.idx - y.idx; // stable
    })
    .map((o) => o.r);
}

// DOI format check (does not verify existence). Accepts optional doi:/URL prefixes.
export function isLikelyValidDOI(value) {
  if (!value) return true; // empty is allowed (optional field)
  const bare = value.trim().replace(/^doi:\s*/i, "").replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
  return /^10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i.test(bare);
}
